import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import moment from 'moment';
import 'moment/locale/ko';
import ProjectTimeline from '../../components/ProjectTimeline';
import { compatApi } from '../../utils/Intercepter';
import useTokenStore from '../../stores/tokens';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';

moment.locale('ko');

function ReadProjectList() {
    const { user } = useTokenStore();
    const organizationCode = user?.currentOrganizationCode || '';

    const [inProgressProjects, setInProgressProjects] = useState([]);
    const [endedProjects, setEndedProjects] = useState([]);
    const [activeProjects, setActiveProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showEditModal, setShowEditModal] = useState(false);
    const [showEndConfirmModal, setShowEndConfirmModal] = useState(false);
    const [projectToEdit, setProjectToEdit] = useState(null);
    const [projectToEnd, setProjectToEnd] = useState(null);
    const [errors, setErrors] = useState({});

    const [endedCurrentPage, setEndedCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const itemsPerPage = 5;

    const filteredEndedProjects = endedProjects.filter(p =>
        p.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.client.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const endedTotalPages = Math.ceil(filteredEndedProjects.length / itemsPerPage);
    const endedPaginatedItems = filteredEndedProjects.slice((endedCurrentPage - 1) * itemsPerPage, endedCurrentPage * itemsPerPage);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await compatApi.post('/project/readProjectList', organizationCode);
            const allProjects = await response.json();
            const today = moment().startOf('day');
            const inProgress = allProjects.filter(p => moment(p.endDate).isSameOrAfter(today));
            const ended = allProjects.filter(p => moment(p.endDate).isBefore(today));
            setInProgressProjects(inProgress);
            setEndedProjects(ended);
            setActiveProjects(inProgress);
        } catch (error) {
            alert('데이터를 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProjectToEdit(prev => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!projectToEdit.projectName) newErrors.projectName = "프로젝트 명을 입력해 주세요.";
        if (!projectToEdit.client) newErrors.client = "고객사 명을 입력해 주세요.";
        if (!projectToEdit.startDate) newErrors.startDate = "시작일을 입력해 주세요.";
        else if (moment(projectToEdit.startDate).isAfter(projectToEdit.endDate)) newErrors.startDate = "시작일은 종료일보다 늦을 수 없습니다.";
        if (!projectToEdit.endDate) newErrors.endDate = "종료일을 입력해 주세요.";
        else if (moment(projectToEdit.endDate).isBefore(projectToEdit.startDate)) newErrors.endDate = "종료일은 시작일보다 빠를 수 없습니다.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCreateClick = () => { setProjectToEdit({ projectName: '', client: '', startDate: '', endDate: '' }); setErrors({}); setShowEditModal(true); };
    const handleEditClick = (project) => {
        setProjectToEdit({ ...project, startDate: format(new Date(project.startDate), 'yyyy-MM-dd'), endDate: format(new Date(project.endDate), 'yyyy-MM-dd') });
        setErrors({}); setShowEditModal(true);
    };
    const handleEndClick = (project) => { setProjectToEnd(project); setShowEndConfirmModal(true); };

    const handleConfirmEndProject = async () => {
        if (!projectToEnd) return;
        const payload = { ...projectToEnd, endDate: moment().format('YYYY-MM-DD') };
        try {
            await compatApi.post('/project/saveProject', payload);
            alert('프로젝트가 종료되었습니다.');
            setShowEndConfirmModal(false); setProjectToEnd(null);
            await loadData();
        } catch (error) { alert(`오류 발생: ${error.message}`); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        const payload = { ...projectToEdit, organizationCode };
        try {
            await compatApi.post('/project/saveProject', payload);
            alert(projectToEdit.projectId ? '프로젝트가 수정되었습니다.' : '프로젝트가 등록되었습니다.');
            setShowEditModal(false); setProjectToEdit(null);
            await loadData();
        } catch (error) { alert(`오류 발생: ${error.message}`); }
    };

    const inputS = {
        width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #ececf2',
        background: '#fafaff', padding: '0 10px', fontSize: 13, color: '#1f1d2e',
        outline: 'none', fontFamily: 'inherit', boxShadow: 'none', boxSizing: 'border-box',
    };
    const labelS = { display: 'block', fontSize: 13, fontWeight: 600, color: '#3a3849', marginBottom: 5 };
    const fieldS = { marginBottom: 14 };

    const thS = { padding: '12px 14px', fontSize: 12, fontWeight: 600, color: '#5d5b6e', background: '#f7f6fb', borderBottom: '1px solid #ececf2', textAlign: 'center', whiteSpace: 'nowrap' };
    const tdS = { padding: '11px 14px', fontSize: 13, color: '#3a3849', borderBottom: '1px solid #f4f3f9', textAlign: 'center', whiteSpace: 'nowrap' };

    const pageBtn = (disabled, active) => ({
        height: 32, minWidth: 32, padding: '0 8px', borderRadius: 6, fontSize: 12.5,
        fontWeight: active ? 700 : 500, cursor: disabled ? 'default' : 'pointer',
        border: active ? 'none' : '1px solid #ececf2',
        background: active ? `linear-gradient(135deg, ${PURPLE}, #9b8de8)` : disabled ? '#fafaff' : 'white',
        color: active ? 'white' : disabled ? '#c7c5d2' : '#5d5b6e',
        fontFamily: 'inherit',
    });

    const Modal = ({ show, onHide, title, children, footer, size }) => {
        if (!show) return null;
        return (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
                onClick={onHide}>
                <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 8px 40px rgba(0,0,0,0.18)', width: '100%', maxWidth: size === 'sm' ? 360 : 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
                    onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #ececf2', flexShrink: 0 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#1f1d2e' }}>{title}</span>
                        <button onClick={onHide} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#7a7989', padding: 0 }}>✕</button>
                    </div>
                    <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>{children}</div>
                    {footer && <div style={{ padding: '12px 20px', borderTop: '1px solid #ececf2', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>{footer}</div>}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 61px)', background: '#f5f4fa' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${hexToRgba(PURPLE, 0.2)}`, borderTopColor: PURPLE, animation: 'spin 0.7s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
            <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

                {/* Edit/Create Modal */}
                <Modal show={showEditModal && !!projectToEdit} onHide={() => setShowEditModal(false)}
                    title={projectToEdit?.projectId ? '프로젝트 수정' : '프로젝트 등록'}
                    footer={
                        <>
                            <button onClick={() => setShowEditModal(false)} style={{ height: 34, padding: '0 14px', borderRadius: 8, border: '1.5px solid #ececf2', background: 'white', fontSize: 13, fontWeight: 600, color: '#5d5b6e', cursor: 'pointer', fontFamily: 'inherit' }}>취소</button>
                            <button onClick={handleSubmit} style={{ height: 34, padding: '0 14px', borderRadius: 8, border: 'none', background: `linear-gradient(135deg, ${PURPLE}, #9b8de8)`, fontSize: 13, fontWeight: 600, color: 'white', cursor: 'pointer', fontFamily: 'inherit' }}>저장</button>
                        </>
                    }>
                    {projectToEdit && (
                        <>
                            <div style={fieldS}>
                                <label style={labelS}>프로젝트명</label>
                                <input type="text" name="projectName" value={projectToEdit.projectName} onChange={handleInputChange}
                                    style={{ ...inputS, borderColor: errors.projectName ? '#ef4444' : '#ececf2' }}
                                    onFocus={e => e.target.style.borderColor = PURPLE}
                                    onBlur={e => e.target.style.borderColor = errors.projectName ? '#ef4444' : '#ececf2'} />
                                {errors.projectName && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.projectName}</div>}
                            </div>
                            <div style={fieldS}>
                                <label style={labelS}>고객사</label>
                                <input type="text" name="client" value={projectToEdit.client} onChange={handleInputChange}
                                    style={{ ...inputS, borderColor: errors.client ? '#ef4444' : '#ececf2' }}
                                    onFocus={e => e.target.style.borderColor = PURPLE}
                                    onBlur={e => e.target.style.borderColor = errors.client ? '#ef4444' : '#ececf2'} />
                                {errors.client && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.client}</div>}
                            </div>
                            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                                <div style={{ flex: 1 }}>
                                    <label style={labelS}>시작일</label>
                                    <input type="date" name="startDate" value={projectToEdit.startDate} onChange={handleInputChange}
                                        style={{ ...inputS, borderColor: errors.startDate ? '#ef4444' : '#ececf2' }}
                                        onFocus={e => e.target.style.borderColor = PURPLE}
                                        onBlur={e => e.target.style.borderColor = errors.startDate ? '#ef4444' : '#ececf2'} />
                                    {errors.startDate && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.startDate}</div>}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={labelS}>종료일</label>
                                    <input type="date" name="endDate" value={projectToEdit.endDate} onChange={handleInputChange}
                                        style={{ ...inputS, borderColor: errors.endDate ? '#ef4444' : '#ececf2' }}
                                        onFocus={e => e.target.style.borderColor = PURPLE}
                                        onBlur={e => e.target.style.borderColor = errors.endDate ? '#ef4444' : '#ececf2'} />
                                    {errors.endDate && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.endDate}</div>}
                                </div>
                            </div>
                        </>
                    )}
                </Modal>

                {/* End Confirm Modal */}
                <Modal show={showEndConfirmModal} onHide={() => setShowEndConfirmModal(false)} title="프로젝트 종료" size="sm"
                    footer={
                        <>
                            <button onClick={() => setShowEndConfirmModal(false)} style={{ height: 34, padding: '0 14px', borderRadius: 8, border: '1.5px solid #ececf2', background: 'white', fontSize: 13, fontWeight: 600, color: '#5d5b6e', cursor: 'pointer', fontFamily: 'inherit' }}>취소</button>
                            <button onClick={handleConfirmEndProject} style={{ height: 34, padding: '0 14px', borderRadius: 8, border: 'none', background: '#dc2626', fontSize: 13, fontWeight: 600, color: 'white', cursor: 'pointer', fontFamily: 'inherit' }}>종료 확인</button>
                        </>
                    }>
                    <p style={{ margin: 0, fontSize: 13.5, color: '#1f1d2e' }}>
                        <strong>{projectToEnd?.projectName}</strong> 프로젝트를 정말로 종료하시겠습니까?
                    </p>
                    <p style={{ margin: '8px 0 0', fontSize: 13, color: '#7a7989' }}>종료일은 오늘 날짜로 설정됩니다.</p>
                </Modal>

                {/* Timeline Card */}
                <div style={{
                    background: 'white', borderRadius: 16, border: '1px solid #f0eff5',
                    boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                    overflow: 'hidden', marginBottom: 20,
                }}>
                    <div style={{ padding: '14px 20px', background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)', borderBottom: '1px solid #ececf2' }}>
                        <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>진행중인 프로젝트</h2>
                    </div>
                    <div style={{ padding: 0 }}>
                        <ProjectTimeline projects={activeProjects} />
                    </div>
                </div>

                {/* In-Progress Card */}
                <div style={{
                    background: 'white', borderRadius: 16, border: '1px solid #f0eff5',
                    boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                    overflow: 'hidden', marginBottom: 20,
                }}>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '14px 20px', background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)', borderBottom: '1px solid #ececf2',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>진행중인 프로젝트 목록</h2>
                            <span style={{ fontSize: 11, fontWeight: 600, background: 'white', color: '#5d5b6e', padding: '2px 8px', borderRadius: 999, border: '1px solid #ececf2' }}>
                                {inProgressProjects.length}건
                            </span>
                        </div>
                        <button onClick={handleCreateClick} style={{
                            height: 34, padding: '0 16px', borderRadius: 8, border: 'none',
                            background: `linear-gradient(135deg, ${PURPLE}, #9b8de8)`,
                            color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        }}>+ 프로젝트 등록</button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                            <thead>
                                <tr>
                                    {['프로젝트', '고객사', '시작일', '종료일', '관리'].map((h, i) => (
                                        <th key={h} style={{ ...thS, textAlign: i === 0 ? 'left' : 'center' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {inProgressProjects.length > 0 ? inProgressProjects.map(p => (
                                    <tr key={p.projectId}
                                        style={{ background: 'white' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#fafaff'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                        <td style={{ ...tdS, textAlign: 'left' }}>{p.projectName}</td>
                                        <td style={tdS}>{p.client}</td>
                                        <td style={tdS}>{format(new Date(p.startDate), 'yyyy-MM-dd')}</td>
                                        <td style={tdS}>{format(new Date(p.endDate), 'yyyy-MM-dd')}</td>
                                        <td style={tdS}>
                                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                                <button onClick={() => handleEditClick(p)} style={{ height: 28, padding: '0 10px', borderRadius: 6, border: '1.5px solid #ececf2', background: 'white', fontSize: 12, color: '#5d5b6e', cursor: 'pointer', fontFamily: 'inherit' }}>수정</button>
                                                <button onClick={() => handleEndClick(p)} style={{ height: 28, padding: '0 10px', borderRadius: 6, border: '1.5px solid #fca5a5', background: 'white', fontSize: 12, color: '#dc2626', cursor: 'pointer', fontFamily: 'inherit' }}>종료</button>
                                                <Link to={`/project-assignments/${p.projectId}`} state={{ ...p }}
                                                    style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1.5px solid ${MINT}`, background: 'white', fontSize: 12, color: MINT, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
                                                    인력관리
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={5} style={{ ...tdS, color: '#aaa9b7', padding: '32px 14px' }}>진행중인 프로젝트가 없습니다.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Ended Card */}
                <div style={{
                    background: 'white', borderRadius: 16, border: '1px solid #f0eff5',
                    boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '14px 20px', background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)', borderBottom: '1px solid #ececf2',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>종료된 프로젝트</h2>
                            <span style={{ fontSize: 11, fontWeight: 600, background: 'white', color: '#5d5b6e', padding: '2px 8px', borderRadius: 999, border: '1px solid #ececf2' }}>
                                {filteredEndedProjects.length}건
                            </span>
                        </div>
                        <input type="text" placeholder="프로젝트명 또는 고객사 검색"
                            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                            style={{
                                height: 34, width: 260, borderRadius: 8, border: '1.5px solid #ececf2',
                                background: '#fafaff', padding: '0 12px', fontSize: 13, color: '#1f1d2e',
                                outline: 'none', fontFamily: 'inherit', boxShadow: 'none',
                            }}
                            onFocus={e => e.target.style.borderColor = PURPLE}
                            onBlur={e => e.target.style.borderColor = '#ececf2'} />
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                            <thead>
                                <tr>
                                    {['프로젝트', '고객사', '시작일', '종료일', '관리'].map((h, i) => (
                                        <th key={h} style={{ ...thS, textAlign: i === 0 ? 'left' : 'center' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {endedPaginatedItems.length > 0 ? endedPaginatedItems.map(p => (
                                    <tr key={p.projectId}
                                        style={{ background: 'white' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#fafaff'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                        <td style={{ ...tdS, textAlign: 'left' }}>{p.projectName}</td>
                                        <td style={tdS}>{p.client}</td>
                                        <td style={tdS}>{format(new Date(p.startDate), 'yyyy-MM-dd')}</td>
                                        <td style={tdS}>{format(new Date(p.endDate), 'yyyy-MM-dd')}</td>
                                        <td style={tdS}>
                                            <Link to={`/project-assignments/${p.projectId}`} state={{ ...p }}
                                                style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1.5px solid ${MINT}`, background: 'white', fontSize: 12, color: MINT, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
                                                인력보기
                                            </Link>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={5} style={{ ...tdS, color: '#aaa9b7', padding: '32px 14px' }}>종료된 프로젝트가 없습니다.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {endedTotalPages > 1 && (
                        <div style={{ padding: '14px 20px', borderTop: '1px solid #ececf2', display: 'flex', justifyContent: 'center', gap: 4 }}>
                            <button onClick={() => setEndedCurrentPage(p => Math.max(1, p - 1))} disabled={endedCurrentPage === 1} style={pageBtn(endedCurrentPage === 1, false)}>‹</button>
                            {Array.from({ length: endedTotalPages }, (_, i) => i + 1).map(n => (
                                <button key={n} onClick={() => setEndedCurrentPage(n)} style={pageBtn(false, n === endedCurrentPage)}>{n}</button>
                            ))}
                            <button onClick={() => setEndedCurrentPage(p => Math.min(endedTotalPages, p + 1))} disabled={endedCurrentPage === endedTotalPages} style={pageBtn(endedCurrentPage === endedTotalPages, false)}>›</button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

export default ReadProjectList;
